import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  UrlTree,
} from '@angular/router';
import { firstValueFrom, take, timeout } from 'rxjs';
import { GlobalAuthResolver } from 'src/app/appServices/global-auth/global-auth.resolver';
import { IRole } from 'src/app/appModels/IRole';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private static readonly PROFILE_ROUTE_WAIT_MS = 10_000;

  constructor(
    private router: Router,
    private tokenService: TokenService,
    private globalAuthResolver: GlobalAuthResolver,
  ) {}

  async canActivate(
    next: ActivatedRouteSnapshot,
  ): Promise<boolean | UrlTree> {
    await this.tokenService.ensureTokenLoaded();
    if (this.tokenService.hasUsableToken()) {
      return this.authorizeRoute(next);
    }

    const unusableToken = this.tokenService.token;
    if (unusableToken) {
      try {
        await this.tokenService.invalidateIfCurrent(unusableToken);
      } catch {
        // Memory is cleared synchronously by invalidateIfCurrent. A native
        // persistence failure must never leave the protected route accessible.
      }
      return this.router.createUrlTree(['/auth/login']);
    }
    return this.router.createUrlTree(['/auth']);
  }

  /** Re-evaluate authentication when navigating between protected children. */
  canActivateChild(
    next: ActivatedRouteSnapshot,
  ): Promise<boolean | UrlTree> {
    return this.canActivate(next);
  }

  private async authorizeRoute(
    route: ActivatedRouteSnapshot,
  ): Promise<boolean | UrlTree> {
    const requiredRoles = this.findRequiredRoles(route);
    if (requiredRoles.length === 0) {
      return true;
    }

    // A deep link can reach the guard before AppInitializer has restored the
    // in-memory session. The persisted token is already known to be usable, so
    // start the same profile lifecycle here when necessary.
    if (!this.globalAuthResolver.isLoggedIn()) {
      this.globalAuthResolver.beginSession();
    }

    let role = this.globalAuthResolver.role;
    if (!role) {
      try {
        const profile = await firstValueFrom(
          this.globalAuthResolver.getProfile().pipe(
            timeout({ first: AuthGuard.PROFILE_ROUTE_WAIT_MS }),
            take(1),
          ),
        );
        role = profile?.role;
      } catch {
        // A 401/403 invalidates the exact token through the interceptor. A
        // transient failure keeps the token but must never grant an unknown
        // role access to an admin-only page.
        if (!this.tokenService.hasUsableToken()) {
          return this.router.createUrlTree(['/auth/login']);
        }
        return this.isAvailableToEveryKnownRole(requiredRoles)
          ? true
          : this.router.createUrlTree(['/pos']);
      }
    }

    // A transient profile outage resolves to null so the session can recover
    // in the background. Routes shared by every known POS role remain safe and
    // usable; redirecting /pos to itself here would create a navigation loop.
    if (!role) {
      return this.isAvailableToEveryKnownRole(requiredRoles)
        ? true
        : this.router.createUrlTree(['/pos']);
    }

    return requiredRoles.includes(role as IRole)
      ? true
      : this.router.createUrlTree(['/pos']);
  }

  private findRequiredRoles(route: ActivatedRouteSnapshot): IRole[] {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const roles = current.data?.['roles'];
      if (Array.isArray(roles) && roles.length > 0) {
        return roles.filter((role): role is IRole =>
          Object.values(IRole).includes(role as IRole),
        );
      }
      current = current.firstChild;
    }
    return [];
  }

  private isAvailableToEveryKnownRole(requiredRoles: IRole[]): boolean {
    return Object.values(IRole).every((role) =>
      requiredRoles.includes(role),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class LoginGuard {
  constructor(
    private router: Router,
    private tokenService: TokenService,
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    await this.tokenService.ensureTokenLoaded();
    if (this.tokenService.hasUsableToken()) {
      return this.router.createUrlTree(['/pos']);
    }

    const unusableToken = this.tokenService.token;
    if (unusableToken) {
      try {
        await this.tokenService.invalidateIfCurrent(unusableToken);
      } catch {
        // The login page must remain reachable even if Preferences cleanup
        // fails; the active in-memory credential has already been removed.
      }
    }
    return true;
  }
}
